const MAX_SIZE = Math.floor(1024 * 1024 * 3.5);

type DataItem = string | number | boolean;
type DataRow = DataItem[];
type DataChunk = DataRow[];
type DataCallback = (data: DataChunk) => Promise<void>;
type Memo = { data: DataChunk; size: number };

interface DataChopper {
  push: (chunk: DataChunk) => void;
  finalizer: () => Promise<void>;
}

export function createDataChopper(callback: DataCallback): DataChopper {
  console.log('Creating DataChopper');
  const chunks: DataChunk[] = [];
  let promise: Promise<void> | null = null;
  let error: Error | null = null;

  async function proceed(): Promise<void> {
    try {
      let memo: Memo = { data: [], size: 0 };

      while (chunks.length) {
        const chunk = chunks[0];
        const cols = +(chunk[0]?.length || 0);

        let row = 0;
        while (row < chunk.length) {
          // Formula for Excel Online: ((cell + 5) * cols + 6) * rows + ((cols * rows) / 3));
          const current = calculateSize(chunk[row]);
          const extra = cols * (memo.data.length + row + 1);

          if (memo.size + current + extra > MAX_SIZE) {
            break;
          }
          memo.size += current;
          row++;
        }

        if (row > 0) {
          memo.data = memo.data.concat(chunk.splice(0, row));
        }

        if (!chunk.length) {
          chunks.shift();
        }

        if (!chunks.length || !row) {
          const { data } = memo;
          memo = { data: [], size: 0 };
          await callback(data);
        }
      }
    } catch (err) {
      error = err instanceof Error ? err : new Error(String(err));
    } finally {
      promise = null;
    }
  }

  return {
    push: (chunk: DataChunk): void => {
      if (error) {
        throw error;
      }

      chunks.push(chunk);

      if (!promise) {
        promise = proceed();
      }
    },
    finalizer: async (): Promise<void> => {
      if (promise) {
        await promise;
      }
      if (error) {
        throw error;
      }
    },
  };
}

function calculateSize(data: DataRow): number {
  return data.reduce<number>((size, item) => size + item.toString().length + 5, 6);
}
